import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import Image from '@alltrails/core/components/Image';
import getUserProfilePhotoUrl from '@alltrails/core/utils/getUserProfilePhotoUrl';
import PhotoSizeDimensions from '@alltrails/types/PhotoSizeDimensions';
import type { LinkInfo, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import AvatarFlair, { type Flair } from './AvatarFlair';
import CustomAvatar from './CustomAvatar';
import styles from './Avatar.module.scss';

type AvatarSize = Size<'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;

const photoSizeByAvatarSize: Record<AvatarSize, PhotoSizeDimensions> = {
  xxs: '50x50',
  xs: '50x50',
  sm: '80x80',
  md: '120x120',
  lg: '160x160',
  xl: '160x160'
};

type AvatarProps = {
  bordered?: boolean;
  className?: string;
  eager?: boolean;
  linkInfo?: LinkInfo;
  onClick?: () => void;
  size?: AvatarSize;
  userId: number;
  userName: string;
  stopPropagation?: boolean;
  flair?: Flair;
};

export default function Avatar({
  bordered = false,
  className,
  eager = false,
  linkInfo,
  onClick,
  size = 'md',
  userId,
  userName,
  stopPropagation,
  flair
}: AvatarProps) {
  const intl = useIntl();
  const [didError, setDidError] = useState(false);
  const publicBaseUrl = usePublicBaseUrl();
  const src = getUserProfilePhotoUrl({ baseUrl: publicBaseUrl, id: userId, size: photoSizeByAvatarSize[size] });

  return (
    <CustomAvatar
      {...setDenaliAttribute('Avatar')}
      bordered={bordered}
      className={classNames(className, styles.userAvatar)}
      linkInfo={linkInfo}
      onClick={onClick}
      size={size}
      stopPropagation={stopPropagation}
    >
      <span className={styles.imageWrapper}>
        {/* Stay on the shared Image, not next/image. A direct next/image import merges async checkout and upsell CSS into first-load chunks and fails the location CSS budget. placeholder="empty" skips the light blur square on hero photos. */}
        <Image
          alt={intl.formatMessage({ defaultMessage: 'Avatar of {name}' }, { name: userName })}
          className={styles.avatarPhoto}
          fill
          // Override the loader so we can explicitly just use the `src` as-is. This will ignore default quality and width arguments from Next. Our image API does not support them and will return a 400.
          loader={({ src }) => src}
          loading={eager ? 'eager' : 'lazy'}
          onError={() => setDidError(true)}
          placeholder="empty"
          src={didError ? '' : src}
        />
      </span>
      {flair && <AvatarFlair flair={flair} size={size} />}
    </CustomAvatar>
  );
}
